/**
 * The search page's three stat pieces — sources, modules, minutes — built the
 * way the Bottom Line ring is: stacked glossy SVG layers in the ring palette,
 * tilted in a CSS 3D stage. Each turns slowly; under reduced motion they sit
 * still (the global prefers-reduced-motion rule stops the animations).
 */

const PALETTE = ['#D63A2E', '#E22A8C', '#6A2BD9', '#3A3BD6', '#2BB3E6'];
const LAYERS = 7;

const shade = (hex: string, k: number) => {
  const n = parseInt(hex.slice(1), 16);
  const f = (v: number) => Math.round(v * k);
  return `rgb(${f(n >> 16)},${f((n >> 8) & 255)},${f(n & 255)})`;
};
/** Colour at t (0–1) along the palette. */
const along = (t: number) => PALETTE[Math.min(PALETTE.length - 1, Math.floor(t * PALETTE.length))];

const S = 92;
const C = S / 2;
const pt = (r: number, deg: number): [number, number] => {
  const a = ((deg - 90) * Math.PI) / 180;
  return [C + r * Math.cos(a), C + r * Math.sin(a)];
};
const arc = (r0: number, r1: number, a0: number, a1: number) => {
  const lg = a1 - a0 > 180 ? 1 : 0;
  const [x0, y0] = pt(r1, a0);
  const [x1, y1] = pt(r1, a1);
  const [x2, y2] = pt(r0, a1);
  const [x3, y3] = pt(r0, a0);
  return `M${x0} ${y0} A${r1} ${r1} 0 ${lg} 1 ${x1} ${y1} L${x2} ${y2} A${r0} ${r0} 0 ${lg} 0 ${x3} ${y3}Z`;
};

/** A tilted stage that stacks `draw(k, top)` LAYERS deep and turns slowly. */
function Stage({ draw, spin = 18, tilt = 60 }: { draw: (k: number, top: boolean) => React.ReactNode; spin?: number; tilt?: number }) {
  return (
    <div style={{ width: S, height: 58, perspective: 500, perspectiveOrigin: '50% 30%', position: 'relative' }} aria-hidden>
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: -18,
          width: S,
          height: S,
          transformStyle: 'preserve-3d',
          transform: `rotateX(${tilt}deg)`,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            transformStyle: 'preserve-3d',
            animation: `rbSpin ${spin}s linear infinite`,
          }}
        >
          <div style={{ position: 'absolute', inset: 14, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(31,26,31,0.22), rgba(31,26,31,0) 70%)', filter: 'blur(5px)', transform: 'translateZ(-8px)' }} />
          {Array.from({ length: LAYERS }, (_, k) => (
            <svg
              key={k}
              width={S}
              height={S}
              viewBox={`0 0 ${S} ${S}`}
              style={{ position: 'absolute', inset: 0, overflow: 'visible', transform: `translateZ(${k * 1.6}px)` }}
            >
              {draw(k, k === LAYERS - 1)}
            </svg>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Sources: a segmented ring, one segment per source drawn on. */
export function SourcesRing({ n, lit }: { n: number; lit?: number }) {
  const count = Math.max(6, Math.min(40, n || 24));
  // Segments not yet done sit as grey ghosts (the build page); by default all are lit.
  const on = lit == null ? count : Math.round(Math.max(0, Math.min(1, lit)) * count);
  const step = 360 / count;
  return (
    <Stage
      spin={26}
      draw={(k, top) => (
        <>
          {top && (
            <defs>
              <linearGradient id="rbSrcGloss" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
                <stop offset="55%" stopColor="rgba(255,255,255,0)" />
              </linearGradient>
            </defs>
          )}
          {Array.from({ length: count }, (_, i) => {
            const col = i < on ? along(i / count) : '#C9C6C9';
            const d = arc(30, 42, i * step + 1.2, (i + 1) * step - 1.2);
            return (
              <g key={i} style={{ transition: 'opacity .4s' }}>
                <path d={d} fill={top ? col : shade(col, 0.5 + k * 0.06)} opacity={i < on ? 1 : 0.55} />
                {top && <path d={d} fill="url(#rbSrcGloss)" style={{ mixBlendMode: 'screen' }} />}
              </g>
            );
          })}
        </>
      )}
    />
  );
}

/** Modules: a small 3D bar city, one column per module, rising and falling. */
export function ModulesBars({ n, lit }: { n: number; lit?: number }) {
  const cols = Math.max(6, Math.min(12, Math.round((n || 22) / 2)));
  const on = lit == null ? cols : Math.round(Math.max(0, Math.min(1, lit)) * cols);
  const W = 92;
  const bw = 6;
  const gap = 1.4;
  const dx = 3.2; // the depth offset that gives each column its side and top faces
  const dy = 2.4;
  const total = cols * (bw + gap) - gap + dx;
  const x0 = (W - total) / 2;
  return (
    <div style={{ width: S, height: 58, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} aria-hidden>
      <svg width={W} height={58} viewBox={`0 0 ${W} 58`} style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id="rbBarGloss" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
            <stop offset="60%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>
        <ellipse cx={W / 2} cy={54} rx={W / 2 - 4} ry={3.5} fill="rgba(31,26,31,0.14)" />
        {Array.from({ length: cols }, (_, i) => {
          const t = i / (cols - 1);
          const col = i < on ? PALETTE[Math.min(PALETTE.length - 1, Math.round(t * (PALETTE.length - 1)))] : '#C9C6C9';
          const h = 12 + 30 * Math.sin((t * 0.92 + 0.06) * Math.PI);
          const x = x0 + i * (bw + gap);
          const base = 52;
          const y = base - h;
          return (
            <g key={i} style={{ transformOrigin: `${x + bw / 2}px ${base}px`, animation: `rbRise 2.8s ease-in-out ${(i * 0.12).toFixed(2)}s infinite alternate` }}>
              {/* side face */}
              <path d={`M${x + bw} ${y} L${x + bw + dx} ${y - dy} L${x + bw + dx} ${base - dy} L${x + bw} ${base} Z`} fill={shade(col, 0.6)} />
              {/* top face */}
              <path d={`M${x} ${y} L${x + dx} ${y - dy} L${x + bw + dx} ${y - dy} L${x + bw} ${y} Z`} fill={col} />
              <path d={`M${x} ${y} L${x + dx} ${y - dy} L${x + bw + dx} ${y - dy} L${x + bw} ${y} Z`} fill="rgba(255,255,255,0.45)" />
              {/* front face */}
              <rect x={x} y={y} width={bw} height={h} fill={col} />
              <rect x={x} y={y} width={bw} height={h} fill="url(#rbBarGloss)" style={{ mixBlendMode: 'screen' }} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Minutes: a tilted dial whose glowing arc fills to the build time (out of five). */
export function MinutesDial({ minutes }: { minutes: number }) {
  const share = Math.max(0.15, Math.min(1, (minutes || 2) / 5));
  const end = 360 * share;
  return (
    <Stage
      spin={14}
      tilt={56}
      draw={(k, top) => (
        <>
          {top && (
            <defs>
              <linearGradient id="rbMinFill" gradientUnits="userSpaceOnUse" x1={pt(40, 0)[0]} y1={pt(40, 0)[1]} x2={pt(40, end)[0]} y2={pt(40, end)[1]}>
                <stop offset="0%" stopColor="#D63A2E" />
                <stop offset="100%" stopColor="#6A2BD9" />
              </linearGradient>
            </defs>
          )}
          <path d={arc(30, 41, 0.5, 359.5)} fill={top ? 'rgba(31,26,31,0.10)' : 'rgba(31,26,31,0.06)'} />
          <path d={arc(30, 41, 0, end)} fill={top ? 'url(#rbMinFill)' : shade('#9A2F7F', 0.55 + k * 0.06)} />
          {top && <circle cx={pt(35.5, end)[0]} cy={pt(35.5, end)[1]} r={4.2} fill="#FFFFFF" stroke="#6A2BD9" strokeWidth={1.6} />}
          {top &&
            Array.from({ length: 12 }, (_, i) => {
              const [x1, y1] = pt(44, i * 30);
              const [x2, y2] = pt(47, i * 30);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(31,26,31,0.3)" strokeWidth={1} />;
            })}
        </>
      )}
    />
  );
}

/**
 * The build page's progress ring: the Bottom Line ring's construction at full
 * size — 36 glossy segments in the palette, lit in turn as the report builds,
 * the rest grey ghosts — tilted and turning slowly.
 */
export function ProgressRing3D({ percent, size = 260 }: { percent: number; size?: number }) {
  const N = 36;
  const on = Math.round((Math.max(0, Math.min(100, percent)) / 100) * N);
  const C2 = size / 2;
  const RO2 = size * 0.46;
  const RI2 = size * 0.37;
  const p2 = (r: number, deg: number): [number, number] => {
    const a = ((deg - 90) * Math.PI) / 180;
    return [C2 + r * Math.cos(a), C2 + r * Math.sin(a)];
  };
  const seg = (a0: number, a1: number) => {
    const [x0, y0] = p2(RO2, a0);
    const [x1, y1] = p2(RO2, a1);
    const [x2, y2] = p2(RI2, a1);
    const [x3, y3] = p2(RI2, a0);
    return `M${x0} ${y0} A${RO2} ${RO2} 0 0 1 ${x1} ${y1} L${x2} ${y2} A${RI2} ${RI2} 0 0 0 ${x3} ${y3}Z`;
  };
  const step = 360 / N;
  const L = 10;
  return (
    <div style={{ width: size, height: size * 0.62, perspective: 900, perspectiveOrigin: '50% 25%', position: 'relative' }} aria-hidden>
      <div style={{ position: 'absolute', left: 0, top: -size * 0.2, width: size, height: size, transformStyle: 'preserve-3d', transform: 'rotateX(62deg)' }}>
        <div style={{ position: 'absolute', inset: 0, transformStyle: 'preserve-3d', animation: 'rbSpin 40s linear infinite' }}>
          <div style={{ position: 'absolute', inset: size * 0.14, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(31,26,31,0.24), rgba(31,26,31,0) 70%)', filter: 'blur(10px)', transform: 'translateZ(-24px)' }} />
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
            <circle cx={C2} cy={C2} r={RO2 + 16} fill="none" stroke="rgba(31,26,31,0.14)" strokeWidth={0.8} />
            <circle cx={C2} cy={C2} r={RO2 + 30} fill="none" stroke="rgba(31,26,31,0.09)" strokeWidth={0.8} strokeDasharray="1 5" />
          </svg>
          {Array.from({ length: L }, (_, k) => {
            const top = k === L - 1;
            return (
              <svg key={k} width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ position: 'absolute', inset: 0, overflow: 'visible', transform: `translateZ(${k * 1.8}px)` }}>
                {top && (
                  <defs>
                    <linearGradient id="rbPrGloss" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
                      <stop offset="50%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>
                  </defs>
                )}
                {Array.from({ length: N }, (_, i) => {
                  const lit = i < on;
                  const col = lit ? along(i / N) : '#CFCCCF';
                  const d = seg(i * step + 1.4, (i + 1) * step - 1.4);
                  return (
                    <g key={i}>
                      <path d={d} fill={top ? col : shade(col, 0.5 + k * 0.05)} opacity={lit ? 1 : 0.5} style={{ transition: 'fill .5s, opacity .5s' }} />
                      {top && <path d={d} fill="url(#rbPrGloss)" style={{ mixBlendMode: 'screen' }} />}
                    </g>
                  );
                })}
              </svg>
            );
          })}
        </div>
      </div>
    </div>
  );
}
